import { z } from 'zod';

/**
 * Fraction exacte et irréductible (architecture §2 : parts successorales en fractions, jamais en flottants).
 * Transportée en JSON sous forme de chaînes de chiffres pour ne rien perdre au-delà de 2^53.
 */
export interface Fraction {
  numerator: string;
  denominator: string;
}

const pgcd = (a: bigint, b: bigint): bigint => {
  let x = a;
  let y = b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
};

export const FractionSchema = z
  .object({
    numerator: z.string().regex(/^(0|[1-9]\d*)$/, 'numérateur entier positif ou nul'),
    denominator: z.string().regex(/^[1-9]\d*$/, 'dénominateur entier strictement positif'),
  })
  .refine(
    (f) => !/^\d+$/.test(f.numerator) || !/^[1-9]\d*$/.test(f.denominator) || pgcd(BigInt(f.numerator), BigInt(f.denominator)) === 1n,
    {
      message: 'fraction non irréductible',
    },
  )
  .meta({ id: 'Fraction', description: 'Fraction exacte et irréductible ; numérateur et dénominateur en chaînes de chiffres.' });

/** Construit une fraction irréductible. Refuse un dénominateur nul et une valeur négative. */
export function fraction(numerateur: bigint | number, denominateur: bigint | number): Fraction {
  const n = BigInt(numerateur);
  const d = BigInt(denominateur);
  if (d <= 0n) throw new RangeError('Dénominateur nul ou négatif');
  if (n < 0n) throw new RangeError('Fraction négative');
  const g = n === 0n ? d : pgcd(n, d);
  return { numerator: String(n / g), denominator: String(d / g) };
}

export const ZERO: Fraction = fraction(0, 1);
export const UN: Fraction = fraction(1, 1);

export const versBigInt = (f: Fraction): { numerator: bigint; denominator: bigint } => ({
  numerator: BigInt(f.numerator),
  denominator: BigInt(f.denominator),
});

export function additionner(a: Fraction, b: Fraction): Fraction {
  const x = versBigInt(a);
  const y = versBigInt(b);
  return fraction(x.numerator * y.denominator + y.numerator * x.denominator, x.denominator * y.denominator);
}

export function multiplier(a: Fraction, b: Fraction): Fraction {
  const x = versBigInt(a);
  const y = versBigInt(b);
  return fraction(x.numerator * y.numerator, x.denominator * y.denominator);
}

export const somme = (fractions: readonly Fraction[]): Fraction => fractions.reduce(additionner, ZERO);

export function comparer(a: Fraction, b: Fraction): -1 | 0 | 1 {
  const x = versBigInt(a);
  const y = versBigInt(b);
  const g = x.numerator * y.denominator;
  const d = y.numerator * x.denominator;
  if (g < d) return -1;
  return g > d ? 1 : 0;
}

export const egales = (a: Fraction, b: Fraction): boolean => comparer(a, b) === 0;
